<script>
export default {
  name: 'Goods'
}

</script>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import DatePicker from "./DatePicker.vue";
import { dateUtil } from '@functions/dateUtil.js';
import { modalUtil } from '@functions/modalUtil.js';
import { url } from '@/config/urls';
import { message } from '@functions/message.js';

// 外部定義メソッド
const { checkDateFormat, examValidDate, formatDateWithSlash } = dateUtil();
const { showModal } = modalUtil();

// emit定義
const emit = defineEmits([
  'setOperatingGoodsData',
  'updateLoading',
  'updateCheckedList',
  'updateAllCheckedList',
  'countCartGoods',
  'addToCart',
  'deleteFromCart',
  'deleteFromFavorite',
  'updateGoodsQuantityList',
  'apiError',
  'validationError',
  'resetCartState',
  'select-drug',
  'quantityChanged'
]);

// prop定義
const props = defineProps({
  /**
   * 画面ID
   *    [発注TOP] OrderSearch
   *    [発注履歴] OrderHistory
   *    [お気に入り詳細] OrderFavoriteDetail
   *    [カート] OrderCart
   */
  screenId: {
      type: String,
      required: true
  },
  /**
   * 画面モード
   *     [カート画面] まとめて削除モード：bulkDelete,納品指定日まとめて編集モード：bulkUpdate
   */
  screenMode: {
      type: String,
      required: false,
      default: 'default'
  },
  isPermittedOrder: {
    type: Boolean,
    required: false,
    default: false
  },
  deliveryDateAllowedFlg: {
    type: Number,
    required: false,
    default: 0
  },
  // 商品情報
  goods: {
    type: Object,
    required: true
  },
  // 商品インデックス
  index: {
    type: Number,
    required: true
  },
  // 商品分類マスタデータ
  goodsTypes: {
    type: Array,
    required: true
  },
  // お気に入りデータ
  goodsFavorites: {
    type: Array,
    required: false,
    default: []
  },
  // お気に入りID（お気に入り詳細画面用）
  goodsFavId: {
    type: Number,
    required: false
  },
  // 商品数量リスト（お気に入り詳細画面用）
  goodsQuantityList: {
    type: Array,
    required: false,
    default: []
  },
  // お気に入り追加API URL
  orderFavoriteItemAddApiUrl: {
    type: String,
    default: url.api.orderFavoriteItem.add
  },
  // お気に入り削除API URL
  orderFavoriteItemDeleteApiUrl: {
    type: String,
    default: url.api.orderFavoriteItem.delete
  },
  // カート更新API URL
  orderCartUpdateApiUrl: {
    type: String,
    default: url.api.orderCart.update
  },
  orderCartCountApiUrl: {
    type: String,
    default: url.api.orderCart.count
  },
  fileIconUrl: {
    type: String,
    default: url.static.fileIcon
  },
  // PMDA添付文書URL
  pmdaBaseUrl: {
    type: String,
    default: url.external.pmda
  },
  // ENIF添付文書URL
  enifBaseUrl: {
    type: String,
    default: url.external.enif
  },
  // ログインURL
  loginUrl: {
    type: String,
    default: url.login
  },
  // エラーURL
  errorUrl: {
    type: String,
    default: url.error
  },
  // チェック済みリスト
  checkedList: {
    type: Array,
    required: false,
    default: []
  },
  // 他ページ込チェック済みリスト
  allCheckedList: {
    type: Array,
    required: false,
    default: []
  }
});

const stockStatusInfo = computed(() => {
  const statusMap = {
    '〇': { img: 'icon_stock1.png', style: { backgroundImage: 'url(/front/assets/common/img/sts_pt1_d.png)', borderColor: 'rgb(0, 116, 191)', color: 'rgb(0, 116, 191)' } },
    '△': { img: 'icon_stock2.png', style: { backgroundImage: 'url(/front/assets/common/img/sts_pt1_b.png)', borderColor: 'rgb(255, 139, 0)', color: 'rgb(255, 139, 0)' } },
    '×': { img: 'icon_stock3.png', style: { backgroundImage: 'url(/front/assets/common/img/sts_pt1_a.png)', borderColor: 'rgb(232, 0, 0)', color: 'rgb(232, 0, 0)' } },
  };
  return statusMap[props.goods.stockSymbol] || null;
});

const stockIconUrl = computed(() => `/front/assets/common/img/${stockStatusInfo.value.img}`);

const elements = {
  orderInfo: ['OrderHistory'], // 発注数ラベル・納品指定日ラベル・発注日時
  orderInfoCheckBox: ['OrderHistory'], // 発注情報チェックボックス
  deleteIcon: ['OrderFavoriteDetail'], // 削除アイコン
  goodsCheckBox: ['OrderCart'], // 商品チェックボックス
  attentionLabel: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // 商品特記事項
  previousOrderInfo: ['OrderSearch', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // 前回発注日・前回発注数量
  addToFavorite: ['OrderSearch', 'OrderHistory', 'OrderCart'], // お気に入りに追加
  goodsType: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // 商品分類
  makerName: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // メーカー名
  wholesalerName: [], // 卸略名
  janCode: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // JANコード
  document: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart'], // 添付文書
  standardIcons: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // 規格アイコン群
  identicalFormulationSearch: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart'], // 同一成分検索
  shippingAdjustedProductFlg: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // 出荷調整品
  stockSymbol: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // 在庫表記記号
  stock: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // 在庫数
  formalGoodsName: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // 商品名
  commonName: ['OrderSearch', 'OrderHistory', 'OrderFavoriteDetail', 'OrderCart', 'OrderScan', 'IdenticalFormulationItemAppModal'], // 一般名
  quantityInputDefault: ['OrderSearch', 'OrderFavoriteDetail'], // 数量入力欄（通常）
  quantityInput: ['OrderCart', 'OrderScan'], // 数量入力欄（カート）
  deliverySpecifiedDateInput: ['OrderCart'], // 納品指定日入力欄
  addToCart: ['OrderSearch', 'OrderFavoriteDetail'], // カートに追加
  selectButton: ['IdenticalFormulationItemAppModal'] // 選択ボタン
}

// OrderScan用: 直前の有効な数量を保持
const previousValidQuantity = ref(1);

// 数量inputがフォーカス中かどうか
const isQuantityFocused = ref(false);

// フォーム入力値
const formInputs = ref({
  quantity: 0,
  deliverySpecifiedDate: ''
});

// エラー
const errors = ref({
  quantity: '',
  deliverySpecifiedDate: ''
});

// カート商品納品指定日変更
const onChangeDeliverySpecifiedDate = () => {
  // バリデーション＆更新処理
  deliverySpecifiedDateValidation();
  if (errors.value.deliverySpecifiedDate !== '') {
    return;
  }
  const janCode = props.goods.janCode;
  const quantity = formInputs.value.quantity;
  const deliverySpecifiedDate = formInputs.value.deliverySpecifiedDate;
  updateCart(janCode, quantity, deliverySpecifiedDate, true);
}
// 納品指定日バリデーション処理
const deliverySpecifiedDateValidation = () => {
  errors.value.deliverySpecifiedDate = '';
  if (formInputs.value.deliverySpecifiedDate === '') {
    return;
  }
  // 日付フォーマットチェック
  if (!checkDateFormat(formInputs.value.deliverySpecifiedDate)) {
    errors.value.deliverySpecifiedDate = message.error.validation.date.format;
    return;
  }
  // 日付論理チェック
  if (!examValidDate(formInputs.value.deliverySpecifiedDate)) {
    errors.value.deliverySpecifiedDate = message.error.validation.date.validity;
    return;
  }
  const inputDate = new Date(formInputs.value.deliverySpecifiedDate);
  const today = new Date();

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  const oneMonthLater = new Date(today);
  oneMonthLater.setMonth(today.getMonth() + 1);
  oneMonthLater.setHours(23, 59, 59, 999);

  // 納品指定日範囲チェック１
  if (inputDate < tomorrow) {
    errors.value.deliverySpecifiedDate = message.error.validation.delivery_specified_date.min;
    return;
  }
  // 納品指定日範囲チェック２
  if (inputDate > oneMonthLater) {
    errors.value.deliverySpecifiedDate = message.error.validation.delivery_specified_date.max;
    return;
  }
  // 納品指定日経過措置日チェック
  if (props.goods.transitionalMeasuresDate != null) {
    const transitionalMeasuresDate = new Date(props.goods.transitionalMeasuresDate);
    if (inputDate < transitionalMeasuresDate) {
      errors.value.deliverySpecifiedDate = message.error.validation.delivery_specified_date.before_transitional_measures_date;
      return;
    }
  }
}
// 数量「＋」
const incrementQuantity = () => {
  // 現在の数量を取得
  let currentQuantity = Number(formInputs.value.quantity);

  // 数値でない場合は 0 にする
  if (isNaN(currentQuantity) || currentQuantity < 0) {
    currentQuantity = 0;
  }

  // +1 して数量を計算
  const quantity = currentQuantity + 1;

  quantityValidation(quantity);
  if (errors.value.quantity !== '' || quantity > 999) {
    emit('validationError', props.goods.janCode, true);
    return;
  }
  updateGoodsQuantityList(quantity);
  if (props.screenId === 'OrderScan') {
    previousValidQuantity.value = formInputs.value.quantity;
    emit('quantityChanged', props.goods.janCode, formInputs.value.quantity);
  }
  if (props.screenId === 'OrderCart') {
    const janCode = props.goods.janCode;
    const quantity = formInputs.value.quantity;
    updateCart(janCode, quantity, null, false);
  }
};
// 数量「－」
const decrementQuantity = () => {
  // 現在の数量を取得
  let currentQuantity = Number(formInputs.value.quantity);

  // 数値でない場合は 2 にする ※最小値1とするため
  if (isNaN(currentQuantity) || currentQuantity < 0) {
    currentQuantity = 2;
  }

  // +1 して数量を計算
  const quantity = currentQuantity - 1;

  quantityValidation(quantity);
  if (errors.value.quantity !== '' || quantity < 0) {
    emit('validationError', props.goods.janCode, true);
    return;
  }
  updateGoodsQuantityList(quantity);
  if (props.screenId === 'OrderScan') {
    previousValidQuantity.value = formInputs.value.quantity;
    emit('quantityChanged', props.goods.janCode, formInputs.value.quantity);
  }
  if (props.screenId === 'OrderCart') {
    const janCode = props.goods.janCode;
    const quantity = formInputs.value.quantity;
    updateCart(janCode, quantity, null, false);
  }
};
// 数量変更
const onInputQuantity = () => {
  // 現在の数量を取得
  let currentQuantity = Number(formInputs.value.quantity);

  //　入力範囲外の入力は強制的に可能な数字に変換する。空白は免除
  if (formInputs.value.quantity === '') {
    return;
  } else if(isNaN(currentQuantity)) {
    // 数字以外は入力不可
    formInputs.value.quantity = '';
  } else if (formInputs.value.quantity <= 0) {
    // カートで入力値が0以下の場合は削除処理を実施
    if (props.screenId === 'OrderCart') {
      emit('validationError', props.goods.janCode, true);
      onClickDeleteFromCart();
      return;
    }
    // OrderScan 用
    if (props.screenId === 'OrderScan') {
      emit('validationError', props.goods.janCode, true, {
        currentQuantity: formInputs.value.quantity,
        previousQuantity: previousValidQuantity.value
      });
      return;
    }
    formInputs.value.quantity = 0;
  } else if (formInputs.value.quantity > 999) {
    formInputs.value.quantity = 999;
  }

  // バリデーションチェック
  quantityValidation(formInputs.value.quantity);
  if (errors.value.quantity !== '') {
    emit('validationError', props.goods.janCode, true);
    return;
  }
  updateGoodsQuantityList(formInputs.value.quantity);
  if (props.screenId === 'OrderScan') {
    previousValidQuantity.value = formInputs.value.quantity;
    emit('quantityChanged', props.goods.janCode, formInputs.value.quantity);
  }
}
const onChangeQuantity = () => {
  // OrderScan: 空の場合はフォーカスアウト時に前回有効値へ復元
  if (props.screenId === 'OrderScan' && formInputs.value.quantity === '') {
    formInputs.value.quantity = previousValidQuantity.value;
    return;
  }
  // 入力確定時のバリデーションチェック
  quantityValidation(formInputs.value.quantity);
  if (errors.value.quantity !== '') {
    emit('validationError', props.goods.janCode, true);
    return;
  }
  if (props.screenId === 'OrderCart' && formInputs.value.quantity <= 0) {
    // カートで入力値が0以下の場合はNG
    emit('validationError', props.goods.janCode, true);
    let errorMessage = message.error.validation.out_of_range;
    errorMessage = errorMessage.replace('{name}', '数量');
    errorMessage = errorMessage.replace('{min}', '1');
    errorMessage = errorMessage.replace('{max}', '999');
    errors.value.quantity = errorMessage;
    return;
  }
  // 確定時にのみカートへ保存する
  if (props.screenId === 'OrderCart') {
    const janCode = props.goods.janCode;
    const quantity = formInputs.value.quantity;
    updateCart(janCode, quantity, null, false);
  }
}
// 数量バリデーション処理
const quantityValidation = (quantity) => {
  errors.value.quantity = '';

  const parsedQuantity = parseInt(quantity);

  // 整数チェック
  if (parsedQuantity !== quantity) {
    let errorMessage = message.error.validation.integer;
    errorMessage = errorMessage.replace('{name}', '数量');
    errors.value.quantity = errorMessage;
  } else if (parsedQuantity < 0 || parsedQuantity > 999) {
   // 範囲チェック
    let errorMessage = message.error.validation.out_of_range;
    errorMessage = errorMessage.replace('{name}', '数量');
    errorMessage = errorMessage.replace('{min}', '0');
    errorMessage = errorMessage.replace('{max}', '999');
    errors.value.quantity = errorMessage;
  } else {
    // 問題なしなら quantity を整数に変換して返す
    formInputs.value.quantity = parsedQuantity;
    emit('validationError', props.goods.janCode, false);
  }
}
// カート商品・納品指定日更新
// 連続更新を防ぐため、updateCartProcessingを使用（デートピッカーのonSelectとonCloseで2回発火してしまうため）
let updateCartProcessing = false;
const updateCart = (janCode, quantity, deliverySpecifiedDate, isUpdateDeliverySpecifiedDate) => {
  if (updateCartProcessing) {
    return;
  }
  updateCartProcessing = true;
  emit('updateLoading', true);
  if (deliverySpecifiedDate) {
    deliverySpecifiedDate = deliverySpecifiedDate.replaceAll('/', '');
  }
  const goods ={
    janCode: janCode,
    quantity: quantity,
    deliverySpecifiedDate: deliverySpecifiedDate
  };
  const param = {
    goodsList: [goods],
    isUpdateDeliverySpecifiedDate: isUpdateDeliverySpecifiedDate
  };
  axios.post(props.orderCartUpdateApiUrl, param)
    .then((response) => {
      emit('countCartGoods');
    })
    .catch((error) => {
      emit('apiError', error);
    })
    .finally(() => {
      emit('updateLoading', false);
      updateCartProcessing = false;
    });
}

// モーダル用商品データ（グローバル状態として管理）
const operatingGoodsData = ref(null);

// モーダルに商品データを設定
const setOperatingGoodsData = () => {
  operatingGoodsData.value = {
    janCode: props.goods.janCode,
    quantity: formInputs.value.quantity,
    goodsName: props.goods.formalGoodsName
  };
  emit('setOperatingGoodsData', operatingGoodsData.value);
};

// お気に入り商品 削除確認
const onClickDeleteFromFavorite = () => {
  // 操作中商品情報の保持
  setOperatingGoodsData();

  // 削除確認モーダルを表示
  showModal('deleteFromFavoriteConfirmModal');
}

// カート商品 追加前重複確認
const onClickAddToCart = () => {

  // 操作中商品情報の保持
  setOperatingGoodsData();
  
  const janCode = props.goods.janCode;
  emit('updateLoading', true);
  axios.get(props.orderCartCountApiUrl, { params: { janCodeList: [janCode] } })
    .then((response) => {

      if (response.data.response.totalNum === 0) {
        // 重複なしのため、警告なしで追加
        emit('addToCart');
      } else {
        // 重複ありのため、警告モーダルを表示
        emit('updateLoading', false);
        showModal('addToCartConfirmModal');
      }
    })
    .catch((error) => {
      emit('updateLoading', false);
      emit('apiError', error);
    })
    .finally(() => {
      // 数量をリセット
      formInputs.value.quantity = 0;
      updateGoodsQuantityList(formInputs.value.quantity);
    });
}

// カート商品 削除確認
const onClickDeleteFromCart = () => {
  // OrderScanの場合はスキャン削除確認モーダルを呼ぶ
  if (props.screenId === 'OrderScan') {
    emit('validationError', props.goods.janCode, true, {
      currentQuantity: 0
      , previousQuantity: formInputs.value.quantity
    });
    return;
  }

  // 操作中商品情報の保持
  setOperatingGoodsData();

  // キーボードの裏にモーダルが隠れないよう、開く前にフォーカスを外しておく
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  // 削除確認モーダルを表示
  showModal('deleteFromCartConfirmModal');
}

// 商品分類名
const goodsTypeName = computed({
  get() {
    if (props.goods.goodsType) {
      const goodsTypes = props.goodsTypes.filter(goodsType => goodsType.code === props.goods.goodsType);
      return goodsTypes[0]?.name || '';
    }
    return '';
  }
});

// お気に入りIDリスト
const goodsFavIds = computed({
  get() {
    if (!props.goods.concatGoodsFavId) {
      return [];
    }
    return props.goods.concatGoodsFavId.split(',').map(id => parseInt(id));
  }
});

// お気に入り追加・削除
const onChangeGoodsFavorite = (event, goodsFavId) => {
  
  emit('updateLoading', true);
  const param = {
    goodsFavId: goodsFavId,
    janCodeList: [props.goods.janCode]
  };
  let apiUrl = null;
  if (event.target.checked) {
    apiUrl = props.orderFavoriteItemAddApiUrl;
  } else {
    apiUrl = props.orderFavoriteItemDeleteApiUrl;
  }
  axios.post(apiUrl, param)
    .then((response) => {
      // do nothing
    })
    .catch((error) => {
      emit('apiError', error);
      // APIエラー時はチェックボックスの状態を元に戻す
      event.target.checked = !event.target.checked;
    })
    .finally(() => {
      emit('updateLoading', false);
      emit('resetCartState'); 
    });
}

// チェックボックス変更
const onChangeCheckBox = (event, value) => {
  const checkedList = [...props.checkedList];
  const allCheckedList = [...props.allCheckedList];
  
  if (event.target.checked) {
    // チェックON時、リストに追加
    if (!checkedList.includes(value)) checkedList.push(value);
    if (!allCheckedList.includes(value)) allCheckedList.push(value);
  } else {
    // チェックOFF時、リストから削除
    const checkedIndex = checkedList.indexOf(value);
    if (checkedIndex > -1) checkedList.splice(checkedIndex, 1);
    
    const allCheckedIndex = allCheckedList.indexOf(value);
    if (allCheckedIndex > -1) allCheckedList.splice(allCheckedIndex, 1);
  }
  
  // 親のcheckedListを更新
  emit('updateCheckedList', checkedList);
  emit('updateAllCheckedList', allCheckedList);
  
}

// 同一製剤モーダル用商品データ
const ordered = computed(() => {
  // TODO:同一成分検索ボタンを非表示にしたいため、常にFALSEを返しています。　#80459
  return false;

  if (!props.goods.salesDetailOrderId) {
    return null;
  }
  return {
    orderId: props.goods.salesDetailOrderId,
    janCode: props.goods.janCode,
    makerName: props.goods.makerName,
    formalGoodsName1: props.goods.salesDetailFormalGoodsName1,
    formalGoodsName2: props.goods.salesDetailFormalGoodsName2,
    shortageAmount: props.goods.salesDetailShortageAmount,
    makerName: props.goods.salesDetailMakerName
  };
});
// 同一成分検索ボタン押下時
const onClickSearchIdenticalFormulation = (event) => {
  emit('updateModalData', ordered.value);
  $('html').css('scroll-behavior', 'auto');
  event.target.click();
}

const onClickSelectDrug = () => {
  emit('select-drug', props.goods);
}

// 数量の設定
const setParams = () => {
  if(props.screenId === 'OrderSearch') {
    // 発注TOP 初期値0
    formInputs.value.quantity = 0;
  } else if (props.screenId === 'OrderScan') {
    formInputs.value.quantity = props.goods.quantity ?? 1;
    previousValidQuantity.value = formInputs.value.quantity;
  } else if (props.screenId === 'OrderFavoriteDetail') {
    
    // お気に入り詳細
    const target = props.goodsQuantityList.find(item => item.janCode === props.goods.janCode);
    if (!props.goods.canOrder) {
      // 発注不可商品の場合 0
      formInputs.value.quantity = 0;
    } else if (target) {
      formInputs.value.quantity = target.quantity;
    } else {
      formInputs.value.quantity = 1;
    }

  } else if (props.screenId === 'OrderCart') {
    // 発注カート 初期値カート商品数量
    formInputs.value.quantity = props.goods.quantity;
    if (props.goods.deliverySpecifiedDate) {
      formInputs.value.deliverySpecifiedDate = formatDateWithSlash(props.goods.deliverySpecifiedDate);
    }
  }
}

// 商品数量リストの変更を通知
const updateGoodsQuantityList = (quantity) => {
  // お気に入り詳細画面のみ
  if (props.screenId !== 'OrderFavoriteDetail') {
    return;
  }
  
  const updatedGoodsQuantityList = [...props.goodsQuantityList];
  const targetQuantity = updatedGoodsQuantityList.find(item => item.janCode === props.goods.janCode);
  
  if (targetQuantity) {
    targetQuantity.quantity = quantity;
  } else {
    // 該当するJANコードが見つからない場合は新しく追加
    updatedGoodsQuantityList.push({
      janCode: props.goods.janCode,
      quantity: quantity
    });
  }
  
  emit('updateGoodsQuantityList', updatedGoodsQuantityList);
}

onMounted(() => {
  // 数量の初期値設定
  setParams();
  // 納品指定日バリデーション
  deliverySpecifiedDateValidation();
});

// propsの変更を監視
watch(() => props.goods, (newGoods, oldGoods) => {
  if (newGoods && oldGoods && newGoods.janCode !== oldGoods.janCode) {
    // 商品が変わった場合のみ処理を実行
    setParams();
    deliverySpecifiedDateValidation();
  }
}, { deep: true });

watch(
    () => props.goods?.quantity,
    (newQty) => {
        if (props.screenId === 'OrderScan' && !isQuantityFocused.value) {
            formInputs.value.quantity = newQty ?? 1;
            previousValidQuantity.value = formInputs.value.quantity;
        }
    }
);

// goodsQuantityListの変更を監視（お気に入り詳細画面のみ）
watch(() => props.goodsQuantityList, (newList) => {
  if (props.screenId === 'OrderFavoriteDetail' && newList) {
    setParams();
  }
}, { deep: true });

// 納品指定日の変更を監視
watch(() => props.goods
, (newGoods) => {
    formInputs.value.quantity = newGoods.quantity ?? 0;

    if (newGoods.deliverySpecifiedDate) {
      formInputs.value.deliverySpecifiedDate =
        formatDateWithSlash(newGoods.deliverySpecifiedDate);
    } else {
      formInputs.value.deliverySpecifiedDate = '';
    }
  },
  { immediate: true, deep: true }
)

// 在庫表記記号などがある場合は、余白を調整
const hasTopRightIcon = computed(() => {
  const sid = props.screenId;

  const hasStockSymbol =
    elements.stockSymbol.includes(sid) && stockStatusInfo.value !== null;

  const hasIdenticalFormulation =
    elements.identicalFormulationSearch.includes(sid) && ordered.value !== false && ordered.value !== null;

  return hasStockSymbol || hasIdenticalFormulation;
});

</script>

<template>
  <div class="simple_card st1" :class="{ cancel: !props.goods.canOrder }">
    <div class="simple_card_body">
      <div class="simple_card_body_content">
        <div class="simple_body-check">
          <template v-if="elements.deleteIcon.includes(props.screenId)">
            <a class="com_i_btn1">
              <img src="/front/assets/common/img/icon_trash3.png" alt="" class="icon_trash3" @click="onClickDeleteFromFavorite">
            </a>
          </template>
          <template v-if="elements.orderInfoCheckBox.includes(props.screenId) && props.goods.canOrder">
            <div class="com_form_check1 st1"><input type="checkbox" :checked="props.allCheckedList.includes(props.goods.orderInfoId)" @change="onChangeCheckBox($event, props.goods.orderInfoId)"><label></label></div>
          </template>
          <template v-if="elements.goodsCheckBox.includes(props.screenId) && ((props.screenMode === 'bulkUpdate') && props.goods.canOrder && props.goods.canSpecifyDate) || (props.screenMode === 'bulkDelete')">
            <div class="com_form_check1 st1"><input type="checkbox" :checked="props.allCheckedList.includes(props.goods.janCode)" @change="onChangeCheckBox($event, props.goods.janCode)"><label></label></div>
          </template>
        </div>
        <div class="goods-info-container">
          <div class="body-upper-half">
            <div class="simple_items2">
              <p v-if="elements.attentionLabel.includes(props.screenId) && props.goods.attentionLabel != null" class="simple_txt_16 simple_red_bold">
                {{ props.goods.attentionLabel }}
              </p>
            </div>
            <div class="simple_items1" v-if="props.isPermittedOrder">
              <p v-if="elements.previousOrderInfo.includes(props.screenId)">
                前回発注：{{ props.goods.previousOrderDate || "-" }}　数量：{{ props.goods.previousOrderQuantity || "-" }}
              </p>
              <p v-if="elements.orderInfo.includes(props.screenId)">
                発注日時：{{ props.goods.orderDateTime }}
              </p>
              <div class="com_dd_list1" v-if="elements.addToFavorite.includes(props.screenId) && props.goodsFavorites.length > 0 && props.isPermittedOrder && !(props.screenId === 'OrderHistory' && !props.goods.existsInGoodsMaster)">
                <p class="txt1" :class="{ 'disabled-text': !props.goods.canOrder }">お気に入りに追加</p>
                <div class="list1" v-if="props.goods.canOrder">
                  <ul>
                    <li v-for="fav in props.goodsFavorites" :key="fav.id">
                      <div class="com_form_check2">
                        <input type="checkbox" 
                            :checked="goodsFavIds.includes(fav.id)"
                            @change="onChangeGoodsFavorite($event, fav.id)">
                        <label>{{ fav.name }}</label>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="grid_container" :class="{ 'no-permitted-order': !props.isPermittedOrder && hasTopRightIcon }">
              <p v-if="elements.goodsType.includes(props.screenId)" class="simple_txt_16 simple_blue_bold">
                {{ goodsTypeName }}
              </p>
              <p class="simple_txt_14">
                <span v-if="elements.janCode.includes(props.screenId)">
                  {{ props.goods.janCode }}
                </span>
                &emsp;
                <template v-if="elements.document.includes(props.screenId)">
                  <template v-if="props.goods.goodsType == '04' && props.goods.documentReferenceNumber !== null && props.goods.documentReferenceNumber !== ''">
                    <a target="_blank" class="com_link2 b" :href="props.enifBaseUrl.replace('{DOCNUM}', props.goods.documentReferenceNumber)">
                      添付文書<img :src="props.fileIconUrl" alt="PDF" class="com_icon_pdf1" />
                    </a>
                  </template>
                  <template v-else-if="props.goods.goodsType != '04' && props.goods.salePackagingUnitCode !== null && props.goods.salePackagingUnitCode !== ''">
                    <a target="_blank" class="com_link2 b" :href="props.pmdaBaseUrl.replace('{GTIN}', props.goods.salePackagingUnitCode)">
                      添付文書<img :src="props.fileIconUrl" alt="PDF" class="com_icon_pdf1" />
                    </a>
                  </template>
                </template>
                <span v-if="elements.standardIcons.includes(props.screenId)">
                  <span v-if="props.goods.generic == '1'" class="com_s_icon1">後</span>
                  <span v-if="props.goods.japanesePharmacopoeia == '1'" class="com_s_icon1">局</span>
                  <span v-if="props.goods.powerfulDrug == '1'" class="com_s_icon2">劇</span>
                </span>
              </p>
              <p class="simple_txt_18">
                <span v-if="elements.makerName.includes(props.screenId)" class="simple_txt_ln1">
                  {{ props.goods.makerName }}
                </span>
                <span v-if="elements.wholesalerName.includes(props.screenId)" class="simple_txt_ln1">
                  {{ props.goods.wholesalerName }}
                </span>
              </p>
              <p class="simple_txt_18">
                <span v-if="elements.formalGoodsName.includes(props.screenId)">
                  {{ props.goods.formalGoodsName }}
                </span>
                <br>
                <span v-if="elements.commonName.includes(props.screenId)" class="simple_txt_sub1">
                  {{ props.goods.commonName }}
                </span>
              </p>
            </div>
          </div>
          <div class="pc-no-display" style="text-align: right;">
            <!-- 出荷調整品 -->
            <div class="com_fs12" v-if="elements.shippingAdjustedProductFlg.includes(props.screenId) && props.goods.shippingAdjustedProductFlg === '1'">
              <span>出荷調整品</span>
            </div>
            <div class="com_status1 simple_status_wrap">
              <!-- 同一製剤注文アイコン -->
              <a v-if="elements.identicalFormulationSearch.includes(props.screenId) && ordered"
                  class="simple_status_wrap_content status_content simple_drug_search dspb-w"
                  data-bs-toggle="modal" data-bs-target="#modal1" @click="onClickSearchIdenticalFormulation($event)">
                <span class="icon3 sts10a drug_search_icon"></span>
              </a>
              <!-- 在庫表記記号 -->
              <div v-if="elements.stockSymbol.includes(props.screenId) && stockStatusInfo"
                  class="simple_status_wrap_content status_content st1"
                  :style="stockStatusInfo.style">
                  <span class="simple_status_txt txt3" :style="{ color: stockStatusInfo.style.color }">在庫</span>
                  <span class="icon4">
                    <img :src="stockIconUrl" alt="">
                  </span>
                  <span v-if="elements.stock.includes(props.screenId) && props.goods.stock != null && props.goods.stock != 0" class="count1">
                    <!--{{ props.goods.stock }}個-->
                  </span>
              </div>
            </div>
          </div>
        </div>
        <div class="body_lower_half">
          <!-- 出荷調整品 -->
          <div class="sp-no-display com_fs12" v-if="elements.shippingAdjustedProductFlg.includes(props.screenId) && props.goods.shippingAdjustedProductFlg === '1'">
            <span>出荷調整品</span>
          </div>
          <div class="sp-no-display" style="margin: auto 0;">
            <div style="display: flex; gap: 5px;">
              <div class="com_status1 simple_status_wrap">
                <!-- 同一製剤注文アイコン -->
                <a v-if="elements.identicalFormulationSearch.includes(props.screenId) && ordered"
                    class="simple_status_wrap_content status_content simple_drug_search dspb-w"
                    data-bs-toggle="modal" data-bs-target="#modal1" @click="onClickSearchIdenticalFormulation($event)">
                  <span class="icon3 sts10a drug_search_icon"></span>
                </a>
                <!-- 在庫表記記号 -->
                <div v-if="elements.stockSymbol.includes(props.screenId) && stockStatusInfo"
                    class="simple_status_wrap_content status_content st1"
                    :style="stockStatusInfo.style">
                    <span class="simple_status_txt txt3" :style="{ color: stockStatusInfo.style.color }">在庫</span>
                    <span class="icon4">
                      <img :src="stockIconUrl" alt="">
                    </span>
                    <span v-if="elements.stock.includes(props.screenId) && props.goods.stock != null && props.goods.stock != 0" class="count1">
                      <!--{{ props.goods.stock }}個-->
                    </span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="props.isPermittedOrder" class="com_ipt_wrap1">
            <template v-if="elements.deliverySpecifiedDateInput.includes(props.screenId) && props.deliveryDateAllowedFlg === 1">
              <div v-if="props.goods.canSpecifyDate" class="datepicker-group1">
                <div>
                  <label :for="`js-deliverySpecifiedDate-datepicker-${props.index}`">納品指定日</label>
                  <DatePicker
                      v-model="formInputs.deliverySpecifiedDate"
                      @update:modelValue="onChangeDeliverySpecifiedDate()"
                      :class="{ error: errors.deliverySpecifiedDate !== '' }"
                      :identifier="`js-deliverySpecifiedDate-datepicker-${props.index}`"
                      :disabled="!props.goods.canOrder"
                  ></DatePicker>
                </div>
              </div>
              <div v-else class="com_flx1">
                <div class="item1">
                  <span class="txt1">納品指定日</span>
                  <span class="txt2">指定不可</span>
                </div>
              </div>
            </template>
            <div v-if="elements.quantityInputDefault.includes(props.screenId)" class="com_count_group1">
              <button type="button" class="com_btn8 l" @click="decrementQuantity()" :disabled="!props.goods.canOrder || formInputs.quantity === 0"><span>−</span></button>
              <input type="number" inputmode="numeric" v-model="formInputs.quantity" @input="onInputQuantity" @change="onChangeQuantity" class="form-control com_form1"
                    :class="{ error: errors.quantity !== '' }" min="1" max="999" :disabled="!props.goods.canOrder">
              <button type="button" class="com_btn8 r" @click="incrementQuantity()" :disabled="!props.goods.canOrder || formInputs.quantity === 999"><span>＋</span></button>
            </div>
            <button v-if="elements.addToCart.includes(props.screenId)" type="button" @click="onClickAddToCart" class="com_btn9" :disabled="!props.goods.canOrder || formInputs.quantity === 0 || errors.quantity !== ''">
              <span>カートに<span>追加</span></span>
            </button>
            <div v-if="elements.quantityInput.includes(props.screenId)" class="com_count_group1">
              <button v-if="!props.goods.canOrder || formInputs.quantity === 1" type="button" class="com_btn8 l cl1" @click="onClickDeleteFromCart"><span><img src="/front/assets/common/img/icon_trash2.png" alt="" class="icon_trash2"></span></button>
              <button v-else type="button" class="com_btn8 l" @click="decrementQuantity()" :disabled="!props.goods.canOrder || formInputs.quantity === 0"><span>−</span></button>
              <input type="number" inputmode="numeric" v-model="formInputs.quantity" @focus="isQuantityFocused = true" @blur="isQuantityFocused = false; onChangeQuantity()" @input="onInputQuantity" class="form-control com_form1"
                    :class="{ error: errors.quantity !== '' }" min="1" max="999" :disabled="!props.goods.canOrder">
              <button type="button" class="com_btn8 r" @click="incrementQuantity()" :disabled="!props.goods.canOrder || formInputs.quantity === 999"><span>＋</span></button>
            </div>
          </div>
          <div v-if="elements.orderInfo.includes(props.screenId)" class="com_ipt_wrap1">
            <div class="com_flx1">
              <div class="item1">
                <template v-if="props.goods.deliverySpecifiedDate != null && props.goods.deliverySpecifiedDate != ''">
                  <span class="txt1">納品指定日</span>
                  <span class="txt3">{{ formatDateWithSlash(props.goods.deliverySpecifiedDate) }}</span>
                </template>
                <span class="txt4">発注数<span>{{ props.goods.orderQuantity }}</span></span>
              </div>
            </div>
          </div>
          <div v-if="elements.selectButton.includes(props.screenId)" class="select-btn-wrap">
            <a
              href=""
              class="com_app_btn3"
              :class="{ disabled: !props.goods.canOrder }"
              :data-bs-dismiss="props.goods.canOrder && props.screenId !== 'IdenticalFormulationItemAppModal' ? 'modal' : null"
              :aria-disabled="!props.goods.canOrder"
              @click.prevent="props.goods.canOrder && onClickSelectDrug()"
            >選択</a>
          </div>
        </div>
      </div>
      <div v-if="errors.deliverySpecifiedDate !== ''" class="simple_card_error1">
        <span class="com_fs12 com_strong1 com_color4">
          {{ errors.deliverySpecifiedDate }}
        </span>
      </div>
      <div v-if="errors.quantity !== ''" class="simple_card_error1">
        <span class="com_fs12 com_strong1 com_color4">
          {{ errors.quantity }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>

.com_dd_list1 .disabled-text {
  background: #BCBCBC;
  border-color: #BCBCBC;
  color: #FFFFFF;
}

.com_dd_list1 .disabled-text:after {
  background-image: none;
}

.select-btn-wrap {
  display: flex;
  justify-content: center;
  width: 100%;
  align-self: stretch;
}

.select-btn-wrap .com_app_btn3.disabled {
  background: #BCBCBC;
  cursor: default;
  pointer-events: none;
}

</style>

